import React from 'react';
import { DataGrid } from '@mui/x-data-grid';

export interface IncidentTableProps {
  rows: any[];
  columns: any[];
  groupColumns: any[];
  tableMode: 'grouped' | 'raw';
  loading: boolean;
  sortModel: any[];
  setSortModel: (model: any[]) => void;
  onRowClick: (params: any) => void | Promise<void>;
  /** Row id (`group_key` in grouped mode, `incident_id` in raw) to highlight when the detail drawer is open. */
  selectedRowId?: string | null;
  selectionActive?: boolean;
}

const IncidentTable: React.FC<IncidentTableProps> = ({
  rows,
  columns,
  groupColumns,
  tableMode,
  loading,
  sortModel,
  setSortModel,
  onRowClick,
  selectedRowId = null,
  selectionActive = false,
}) => {
  return (
    <DataGrid
      rows={rows}
      columns={(tableMode === 'grouped' ? groupColumns : columns) as any}
      getRowId={(row) => row.group_key || row.episode_id || row.id || row.incident_id}
      getRowClassName={(params) => {
        if (!selectionActive || !selectedRowId) return '';
        const id = String(params.row?.group_key || params.row?.episode_id || params.row?.id || params.row?.incident_id || '');
        if (id && id === String(selectedRowId)) return 'incident-row--drawer-open';
        return '';
      }}
      loading={loading}
      autoHeight
      getRowHeight={() => 94}
      columnHeaderHeight={44}
      pageSizeOptions={[10, 25, 50]}
      sortModel={sortModel}
      onSortModelChange={(m) => setSortModel(m)}
      initialState={{
        pagination: { paginationModel: { pageSize: 10 } },
      }}
      disableRowSelectionOnClick
      onRowClick={onRowClick}
      sx={{
        minHeight: 400,
        border: 0,
        '& .MuiDataGrid-columnHeaders': {
          borderBottom: '1px solid #d8dee8',
          backgroundColor: '#f6f8fb',
          fontWeight: 700,
        },
        '& .MuiDataGrid-columnHeaderTitle': {
          fontWeight: 800,
          color: '#344054',
        },
        // DataGrid auto-focuses the first header on mount using :focus (not keyboard-only).
        // Keep a visible ring for :focus-visible / .Mui-focusVisible keyboard navigation.
        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
          outline: 'none',
        },
        '& .MuiDataGrid-columnHeader:focus-visible, & .MuiDataGrid-columnHeader.Mui-focusVisible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '-2px',
        },
        '& .MuiDataGrid-cell': {
          borderBottom: '1px solid #e5eaf1',
          alignItems: 'center',
          py: 1.2,
        },
        '& .MuiDataGrid-row': {
          cursor: 'pointer',
          transition: 'background-color 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
          '&:hover': {
            backgroundColor: '#f8fbff',
          },
        },
        '& .MuiDataGrid-row.incident-row--drawer-open': {
          backgroundColor: 'rgba(29, 79, 145, 0.1) !important',
          boxShadow: 'inset 5px 0 0 #1d4f91',
        },
        '& .MuiDataGrid-footerContainer': {
          borderTop: '1px solid #e5e7eb',
        },
      }}
    />
  );
};

export default IncidentTable;
